/**
 * Cross-tab coordination for the SyncEngine cold-start, built on the Web Locks
 * API (`navigator.locks`).
 *
 * The problem it solves: every open tab mounts its own SyncEngine and, on boot,
 * runs the heavy cold-start REST pull (products / partners / pre-orders / …).
 * With several tabs open — the exact case users hit — those cold-starts fire
 * simultaneously, hammering the V2 API with duplicate full-table pulls and
 * piling concurrent writes onto the shared IndexedDB. Under that contention a
 * boot can wedge (ALB 460 client-timeouts on the API side, a starved event
 * loop, a blocked DB open) and the splash never clears.
 *
 * The fix: serialize the cold-start behind ONE cross-tab exclusive lock. The
 * first tab to boot becomes the de-facto leader and pulls; the others queue on
 * the lock. Because Dexie 4 live-queries are cross-tab reactive, a queued tab's
 * boot screen still clears the moment the leader writes the critical `_syncMeta`
 * rows — it doesn't have to win the lock to become ready. By the time a queued
 * tab does acquire the lock, the cursors are already populated, so its own
 * cold-start is a fast near-no-op instead of a second full pull.
 *
 * Safety valves:
 *  - No Web Locks support (older Safari, SSR, tests) → run `fn` directly, i.e.
 *    exactly today's per-tab behaviour. Never worse than the status quo.
 *  - A leader that HANGS while holding the lock must not wedge every other tab
 *    forever. We bound the wait with an `AbortSignal`; if the lock can't be
 *    acquired in time we give up waiting and run `fn` unlocked (contending is
 *    strictly better than hanging). Web Locks auto-release when the holding tab
 *    is closed/crashes, so the common "leader tab closed mid-boot" case needs
 *    no special handling — the next queued tab simply acquires and proceeds.
 */

/** Exclusive lock name gating the cold-start pull across all tabs. */
export const COLD_START_LOCK = "stockline-offline-coldstart"

/**
 * How long a tab waits to acquire the cold-start lock before giving up and
 * running its cold-start unlocked. Sized above a healthy cold-start (a second
 * or two) with generous headroom, yet safely UNDER the boot watchdog's stall
 * fuse (`STALL_MS` = 15s in `useBootRecovery`), so a queued tab behind a hung
 * leader falls back to (bounded) contention and can still become ready BEFORE
 * the recovery panel would ever tell the user the boot is stuck.
 */
export const LOCK_ACQUIRE_TIMEOUT_MS = 10_000

/** Info handed to the guarded fn so callers can emit contention telemetry. */
export interface ColdStartLockInfo {
  /** Milliseconds spent waiting to acquire the lock (0 when uncontended). */
  waitedMs: number
  /** True when the lock was acquired; false when we ran unlocked (fallback). */
  locked: boolean
}

function getLockManager(): LockManager | null {
  if (typeof navigator === "undefined") return null
  // `navigator.locks` is undefined on older Safari and in non-secure contexts.
  return navigator.locks ?? null
}

/** True when this environment can coordinate cold-start across tabs. */
export function supportsTabLeadership(): boolean {
  return getLockManager() !== null
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError"
}

/**
 * Run `fn` while holding the cross-tab cold-start lock, so at most one tab
 * executes the heavy pull at a time. Falls back to running `fn` directly when
 * Web Locks are unavailable, or unlocked when the lock can't be acquired within
 * {@link LOCK_ACQUIRE_TIMEOUT_MS} (a hung leader must never wedge other tabs).
 * The lock is released as soon as `fn` settles.
 */
export async function withColdStartLock<T>(
  fn: (info: ColdStartLockInfo) => Promise<T>
): Promise<T> {
  const locks = getLockManager()
  if (!locks) return fn({ waitedMs: 0, locked: false })

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), LOCK_ACQUIRE_TIMEOUT_MS)
  const startedAt = Date.now()
  // Once the callback has begun, the lock was acquired — so an AbortError
  // thrown from WITHIN `fn` (e.g. an aborted fetch during the cold-start) must
  // NOT be mistaken for an acquisition timeout, or we'd run the whole
  // cold-start a SECOND time unlocked after it already ran under the lock.
  let callbackStarted = false

  try {
    return await locks.request(
      COLD_START_LOCK,
      { mode: "exclusive", signal: controller.signal },
      () => {
        callbackStarted = true
        return fn({ waitedMs: Date.now() - startedAt, locked: true })
      }
    )
  } catch (err) {
    // Timed out WAITING for a (likely hung) leader — the abort fired before the
    // callback ever ran — proceed unlocked rather than hang. Any other error
    // (including an abort from inside `fn`) is a genuine failure; surface it.
    if (!callbackStarted && controller.signal.aborted && isAbortError(err)) {
      return fn({ waitedMs: Date.now() - startedAt, locked: false })
    }
    throw err
  } finally {
    clearTimeout(timer)
  }
}
